import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../utils/cn';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  role?: 'dialog' | 'alertdialog';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /** Extra class on the backdrop, e.g. for a legacy E2E selector contract. */
  overlayClassName?: string;
}

const sizeClasses: Record<NonNullable<DialogProps['size']>, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
};

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Portalled dialog. Overlay + Escape + a simple focus trap (focus first focusable on open,
 * Tab/Shift+Tab cycle within the panel, restore focus on close). On mobile (<640px) it renders
 * as a bottom sheet purely via responsive classes.
 */
export const Dialog: React.FC<DialogProps> = ({ open, onClose, title, description, children, footer, role = 'dialog', size = 'md', className, overlayClassName }) => {
  const titleId = useId();
  const descId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const focusable = panel?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    (focusable ?? panel)?.focus();

    return () => {
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      {/* zIndex pinned inline so a legacy global `.modal-overlay` rule (kept only as an
          E2E selector) can never out-stack this backdrop above the panel below. */}
      <div
        className={cn('fixed inset-0 bg-scrim/60', overlayClassName)}
        style={{ zIndex: 0 }}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={cn(
          'relative max-h-[90vh] w-full overflow-y-auto rounded-t-card border border-line bg-card sm:rounded-card',
          sizeClasses[size],
          className
        )}
      >
        <div className="p-4 sm:p-6">
          <h2 id={titleId} className="font-display text-lg font-semibold text-ink">
            {title}
          </h2>
          {description && (
            <p id={descId} className="mt-1 text-sm text-muted">
              {description}
            </p>
          )}
          {children && <div className="mt-4">{children}</div>}
        </div>
        {footer && <div className="flex justify-end gap-2 border-t border-line p-4 sm:p-6">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};
